import axiosInstance from '../../../shared/api/axiosInstance';
import type { PipelineDashboard } from '../../../generated/api/models/pipelineDashboard';
import type { RottingDealListResponse } from '../../../generated/api/models/rottingDealListResponse';
import type { LeadFunnelReport } from '../../../generated/api/models/leadFunnelReport';
import type { DealBulkAssignOwnerResponse } from '../../../generated/api/models/dealBulkAssignOwnerResponse';
import type {
  Pipeline,
  PipelineCreate,
  PipelineUpdate,
  PipelineDefaults,
  CustomerSegment,
  Stage,
  StageCreate,
  StageUpdate,
  StageCriterion,
  StageCriterionCreate,
  StageCriterionUpdate,
  CriterionCompletionStatus,
  CriterionCompletionUpdate,
  AdvanceDealRequest,
  Deal,
  DealCreate,
  DealUpdate,
  PipelineSummaryResponse,
  ListDealsParams,
  DealListFilterParams,
  DealAggregatesResponse,
  PaginatedResponse,
} from '../types';

const BASE_URL = '/api/v1/pipeline';

// Pipeline API
export const pipelineApi = {
  list: async (includeCriteria = false): Promise<Pipeline[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/pipelines`, {
      params: includeCriteria ? { include_criteria: true } : undefined,
    });
    return response.data.data;
  },

  get: async (id: string, includeCriteria = false): Promise<Pipeline> => {
    const response = await axiosInstance.get(`${BASE_URL}/pipelines/${id}`, {
      params: includeCriteria ? { include_criteria: true } : undefined,
    });
    return response.data.data;
  },

  getDefaults: async (
    segment?: CustomerSegment,
    language?: string,
  ): Promise<PipelineDefaults> => {
    const response = await axiosInstance.get(`${BASE_URL}/pipelines/defaults`, {
      params: {
        ...(segment ? { segment } : {}),
        ...(language ? { language } : {}),
      },
    });
    return response.data.data;
  },

  create: async (data: PipelineCreate): Promise<Pipeline> => {
    const response = await axiosInstance.post(`${BASE_URL}/pipelines`, data);
    return response.data.data;
  },

  update: async (id: string, data: PipelineUpdate): Promise<Pipeline> => {
    const response = await axiosInstance.put(`${BASE_URL}/pipelines/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete a pipeline.
   *
   * `moveDealsTo` names another pipeline to take every live {{deal}} this delete
   * would otherwise orphan; without it the route REFUSES an occupied pipeline
   * (PIPELINE_IN_USE 409) rather than soft-deleting it out from under its deals.
   * The move and the delete land in one transaction — two calls could fail
   * between them and leave the deals on a pipeline that no longer exists.
   */
  delete: async (id: string, moveDealsTo?: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/pipelines/${id}`, {
      params: moveDealsTo ? { move_deals_to: moveDealsTo } : undefined,
    });
  },

  getSummary: async (id: string): Promise<PipelineSummaryResponse> => {
    const response = await axiosInstance.get(`${BASE_URL}/pipelines/${id}/summary`);
    return response.data.data;
  },

  // Stage operations
  createStage: async (pipelineId: string, data: StageCreate): Promise<Stage> => {
    const response = await axiosInstance.post(`${BASE_URL}/pipelines/${pipelineId}/stages`, data);
    return response.data.data;
  },

  updateStage: async (stageId: string, data: StageUpdate): Promise<Stage> => {
    const response = await axiosInstance.put(`${BASE_URL}/stages/${stageId}`, data);
    return response.data.data;
  },

  deleteStage: async (pipelineId: string, stageId: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/pipelines/${pipelineId}/stages/${stageId}`);
  },

  reorderStages: async (pipelineId: string, stageIds: string[]): Promise<Pipeline> => {
    const response = await axiosInstance.post(`${BASE_URL}/pipelines/${pipelineId}/stages/reorder`, { stage_ids: stageIds });
    return response.data.data;
  },

  // Stage criteria operations
  listCriteria: async (stageId: string): Promise<StageCriterion[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/stages/${stageId}/criteria`);
    return response.data.data;
  },

  createCriterion: async (stageId: string, data: StageCriterionCreate): Promise<StageCriterion> => {
    const response = await axiosInstance.post(`${BASE_URL}/stages/${stageId}/criteria`, data);
    return response.data.data;
  },

  updateCriterion: async (stageId: string, criterionId: string, data: StageCriterionUpdate): Promise<StageCriterion> => {
    const response = await axiosInstance.put(`${BASE_URL}/stages/${stageId}/criteria/${criterionId}`, data);
    return response.data.data;
  },

  deleteCriterion: async (stageId: string, criterionId: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/stages/${stageId}/criteria/${criterionId}`);
  },

  reorderCriteria: async (stageId: string, criterionIds: string[]): Promise<StageCriterion[]> => {
    const response = await axiosInstance.post(`${BASE_URL}/stages/${stageId}/criteria/reorder`, { criterion_ids: criterionIds });
    return response.data.data;
  },
};


/**
 * What a destructive deal bulk action applies to (`ui-list-pages.md` §11).
 *
 * `matching` carries the FILTER SET rather than ids, because the browser cannot
 * enumerate rows it never fetched. Exactly one arm travels — the server refuses a
 * body carrying both scopes rather than silently choosing one.
 */
export type DealBulkScope =
  | { kind: 'ids'; ids: string[] }
  | { kind: 'matching'; filters: DealListFilterParams };

/** The scope half of a bulk request body — exactly one arm, matching the server's validator. */
const dealBulkScopeBody = (scope: DealBulkScope) =>
  scope.kind === 'matching'
    ? { match_all_filtered: true, filters: scope.filters }
    : { ids: scope.ids };

// Deal API
export const dealApi = {
  list: async (params?: ListDealsParams): Promise<PaginatedResponse<Deal>> => {
    const response = await axiosInstance.get(`${BASE_URL}/deals`, { params });
    return response.data;
  },

  /**
   * Aggregates over the filtered-but-unpaged set (ui-list-pages.md §11).
   * Takes DealListFilterParams, so a page window cannot be passed in.
   */
  aggregates: async (params?: DealListFilterParams): Promise<DealAggregatesResponse> => {
    const response = await axiosInstance.get(`${BASE_URL}/deals/aggregates`, { params });
    return response.data;
  },

  get: async (id: string): Promise<Deal> => {
    const response = await axiosInstance.get(`${BASE_URL}/deals/${id}`, {
      params: { include_contacts: true, include_company: true },
    });
    return response.data.data;
  },

  create: async (data: DealCreate): Promise<Deal> => {
    const response = await axiosInstance.post(`${BASE_URL}/deals`, data);
    return response.data.data;
  },

  update: async (id: string, data: DealUpdate): Promise<Deal> => {
    const response = await axiosInstance.put(`${BASE_URL}/deals/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/deals/${id}`);
  },

  moveToStage: async (dealId: string, stageId: string, extra?: { loss_reason?: string; competitor?: string; actual_close_date?: string }): Promise<Deal> => {
    const response = await axiosInstance.put(`${BASE_URL}/deals/${dealId}/stage`, { stage_id: stageId, ...extra });
    return response.data.data;
  },

  getCriteriaStatus: async (dealId: string, stageId?: string): Promise<CriterionCompletionStatus[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/deals/${dealId}/criteria-status`, {
      params: stageId ? { stage_id: stageId } : undefined,
    });
    return response.data.data;
  },

  updateCriteriaCompletions: async (dealId: string, completions: CriterionCompletionUpdate[]): Promise<CriterionCompletionStatus[]> => {
    const response = await axiosInstance.post(`${BASE_URL}/deals/${dealId}/criteria-completions`, { completions });
    return response.data.data;
  },

  advance: async (dealId: string, data?: AdvanceDealRequest): Promise<Deal> => {
    const response = await axiosInstance.post(`${BASE_URL}/deals/${dealId}/advance`, data || {});
    return response.data.data;
  },

  addContact: async (
    dealId: string,
    contactId: string,
    role?: string,
    isPrimary?: boolean
  ): Promise<void> => {
    await axiosInstance.post(`${BASE_URL}/deals/${dealId}/contacts`, {
      contact_id: contactId,
      role: role || 'involved',
      is_primary: isPrimary,
    });
  },

  updateContact: async (
    dealId: string,
    contactId: string,
    data: { role?: string; is_primary?: boolean }
  ): Promise<void> => {
    await axiosInstance.put(`${BASE_URL}/deals/${dealId}/contacts/${contactId}`, data);
  },

  removeContact: async (dealId: string, contactId: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/deals/${dealId}/contacts/${contactId}`);
  },

  duplicate: async (id: string): Promise<Deal> => {
    const response = await axiosInstance.post(`${BASE_URL}/deals/${id}/duplicate`);
    return response.data.data;
  },

  archive: async (id: string): Promise<Deal> => {
    const response = await axiosInstance.post(`${BASE_URL}/deals/${id}/archive`);
    return response.data.data;
  },

  unarchive: async (id: string): Promise<Deal> => {
    const response = await axiosInstance.post(`${BASE_URL}/deals/${id}/unarchive`);
    return response.data.data;
  },

  bulkDelete: async (scope: DealBulkScope): Promise<{ deleted: number }> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/deals/bulk-delete`,
      dealBulkScopeBody(scope),
    );
    return response.data;
  },

  /**
   * Restore soft-deleted deals — id-scoped ONLY, deliberately.
   *
   * A soft-deleted deal is absent from the deal list's filtered query, so there
   * is no filter set that names the rows a restore would act on; the backend
   * request model (`BulkDealRestoreRequest`) still extends `BulkIdsRequest` for
   * exactly that reason. `DealList` therefore suppresses the delete toast's Undo
   * when the selection was escalated, rather than offering one that would restore
   * only the loaded page.
   */
  bulkRestore: async (ids: string[]): Promise<{ restored: number }> => {
    const response = await axiosInstance.post(`${BASE_URL}/deals/bulk-restore`, { ids });
    return response.data;
  },

  bulkArchive: async (scope: DealBulkScope): Promise<{ archived: number }> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/deals/bulk-archive`,
      dealBulkScopeBody(scope),
    );
    return response.data;
  },

  /**
   * Reassign deals to one owner, scoped EITHER by an explicit id list OR by the
   * filter set (`ui-list-pages.md` §11 → Select-all-matching).
   *
   * The escalated call sends `filters` — the SAME object the list request was
   * built from — because the browser cannot enumerate rows it never fetched. The
   * server re-derives the tenant, company and sharing bounds itself; these filters
   * can only ever narrow. Exactly one scope may be given, enforced server-side.
   *
   * Typed off the GENERATED response model rather than a hand-written shape, so a
   * field added to the schema — `skipped_not_owner`, the shortfall's reason —
   * cannot reach the wire without reaching the caller.
   */
  bulkAssignOwner: async (
    scope: { ids: string[] } | { matchAllFiltered: true; filters: DealListFilterParams },
    /**
     * `null` CLEARS the owner — "Unassigned" is a state the list renders an Owner
     * column and an Owner filter for, and this is the way back to it. It is a
     * VALUE, never an omission: the server takes `owner_id` as nullable but still
     * REQUIRED, so a dropped key is a 422 rather than a silent clear.
     */
    ownerId: string | null
  ): Promise<DealBulkAssignOwnerResponse> => {
    const body =
      'ids' in scope
        ? { ids: scope.ids, owner_id: ownerId }
        : { match_all_filtered: true, filters: scope.filters, owner_id: ownerId };
    const response = await axiosInstance.post(`${BASE_URL}/deals/bulk-assign-owner`, body);
    return response.data;
  },
};


// Deal Task Board API
export interface DealBoardInfo {
  board_id: string | null;
  workspace_id: string | null;
  has_board: boolean;
}

export interface DealBoardCreateResult {
  board_id: string;
  workspace_id: string | null;
  is_new: boolean;
}

export const dealBoardApi = {
  /**
   * Get the task board for a deal (if it exists).
   */
  get: async (dealId: string): Promise<DealBoardInfo> => {
    const response = await axiosInstance.get(`${BASE_URL}/deals/${dealId}/board`);
    return response.data.data;
  },

  /**
   * Create or get the task board for a deal.
   * Creates a new board if one doesn't exist, returns existing board otherwise.
   */
  create: async (dealId: string): Promise<DealBoardCreateResult> => {
    const response = await axiosInstance.post(`${BASE_URL}/deals/${dealId}/board`);
    return response.data.data;
  },
};

// Setup Status API (lightweight, used by sidebar)
export interface SalesSetupStatus {
  has_companies: boolean;
  has_contacts: boolean;
  has_deals: boolean;
}

export const setupStatusApi = {
  get: async (): Promise<SalesSetupStatus> => {
    const response = await axiosInstance.get(`${BASE_URL}/setup-status`);
    return response.data.data;
  },
};

// Dashboard API
export const dashboardApi = {
  get: async (): Promise<PipelineDashboard> => {
    const response = await axiosInstance.get(`${BASE_URL}/dashboard`);
    return response.data;
  },
};

// Reports API
export interface WinLossReport {
  period_days: number;
  won: { count: number; total_value: number; avg_value: number; unconvertible_count: number };
  lost: { count: number; total_value: number; avg_value: number; unconvertible_count: number };
  win_rate: number;
  total_closed: number;
  total_deals?: number;
  open_deals?: number;
}

export interface PipelineVelocityStage {
  stage_id: string;
  name: string;
  position: number;
  stage_type: string;
  avg_days: number;
  deal_count: number;
}

export interface PipelineVelocityReport {
  pipeline_id: string;
  period_days: number;
  stages: PipelineVelocityStage[];
  avg_total_days: number;
}

/**
 * A row of GET /deals/rotting — the generated deal read shape, on which the four
 * rotting-only keys (`days_in_stage`, `rotting_days_limit`, `days_overdue`,
 * `stage_name`) are declared as optional enrichments. Derived from the generated
 * envelope rather than hand-written: the interface this replaced narrowed the
 * payload to 10 fields and typed `amount` / `stage_name` as non-nullable, both of
 * which the wire has always been free to send as null.
 */
export type RottingDeal = RottingDealListResponse['data'][number];

export interface ConversionFunnelStage {
  stage_id: string;
  name: string;
  position: number;
  stage_type: string;
  deals_reached: number;
  conversion_rate: number;
}

export interface ConversionFunnelReport {
  pipeline_id: string | null;
  period_days: number;
  stages: ConversionFunnelStage[];
}

export interface ForecastMonth {
  month: string;
  deal_count: number;
  total_value: number;
  weighted_value: number;
  // Deals whose amount could not be stated in the base currency (ERP P13), so the
  // month's total/weighted values understate reality by this many deals.
  unconvertible_count: number;
}

export interface ForecastReport {
  pipeline_id: string | null;
  months_ahead: number;
  months: ForecastMonth[];
  totals: {
    deal_count: number;
    total_value: number;
    weighted_value: number;
    unconvertible_count: number;
  };
}

export interface RevenueTrendMonth {
  month: string;
  deal_count: number;
  total_value: number;
  unconvertible_count: number;
}

export interface RevenueTrendsReport {
  period_days: number;
  months: RevenueTrendMonth[];
  totals: {
    deal_count: number;
    total_value: number;
    unconvertible_count: number;
  };
}

export interface LeaderboardEntry {
  owner_id: string;
  name: string;
  avatar_url: string | null;
  deals_won: number;
  total_value: number;
  avg_value: number;
  // Won deals this rep closed in a currency that couldn't be stated in the base
  // currency (ERP P13) — excluded from total_value / avg_value.
  unconvertible_count: number;
}

export interface TeamPerformanceMember {
  user_id: string;
  name: string;
  avatar_url: string | null;
  is_current_user: boolean;
  open_deals_count: number;
  open_deals_value: number;
  // Open deals in a currency that couldn't be stated in the base currency (ERP
  // P13) — excluded from open_deals_value.
  open_deals_unconvertible_count: number;
  won_deals_count: number;
  won_deals_value: number;
  // Won deals in a currency that couldn't be stated in the base currency —
  // excluded from won_deals_value.
  won_deals_unconvertible_count: number;
  win_rate: number;
}

function addOwnerIds(params: Record<string, string | number>, ownerIds?: string[]) {
  if (ownerIds && ownerIds.length > 0) {
    params.owner_ids = ownerIds.join(',');
  }
}

export const reportsApi = {
  getWinLoss: async (days: number = 30, ownerIds?: string[]): Promise<WinLossReport> => {
    const params: Record<string, string | number> = { days };
    addOwnerIds(params, ownerIds);
    const response = await axiosInstance.get(`${BASE_URL}/reports/win-loss`, { params });
    return response.data.data;
  },

  getPipelineVelocity: async (pipelineId?: string, days: number = 90, ownerIds?: string[]): Promise<PipelineVelocityReport> => {
    const params: Record<string, string | number> = { days };
    if (pipelineId) params.pipeline_id = pipelineId;
    addOwnerIds(params, ownerIds);
    const response = await axiosInstance.get(`${BASE_URL}/reports/pipeline-velocity`, { params });
    return response.data.data;
  },

  getRottingDeals: async (pipelineId?: string, ownerIds?: string[]): Promise<RottingDeal[]> => {
    const params: Record<string, string> = {};
    if (pipelineId) params.pipeline_id = pipelineId;
    addOwnerIds(params as Record<string, string | number>, ownerIds);
    const response = await axiosInstance.get<RottingDealListResponse>(
      `${BASE_URL}/deals/rotting`,
      { params },
    );
    return response.data.data;
  },

  getConversionFunnel: async (pipelineId?: string, days: number = 90, ownerIds?: string[]): Promise<ConversionFunnelReport> => {
    const params: Record<string, string | number> = { days };
    if (pipelineId) params.pipeline_id = pipelineId;
    addOwnerIds(params, ownerIds);
    const response = await axiosInstance.get(`${BASE_URL}/reports/conversion-funnel`, { params });
    return response.data.data;
  },

  /**
   * The Leads tab's whole report: ONE request, because every metric is drawn
   * from the same cohort (the leads created inside the window) and six requests
   * could disagree about the denominator they share.
   *
   * `owner_ids` goes on the wire as REPEATED BARE KEYS — the shared instance's
   * serializer does that for an array — rather than the comma-joined string the
   * deal reports above still send. The route declares `list[UUID]`, which is the
   * multi-value shape rules-list-endpoints.md mandates for anything new.
   */
  getLeadFunnel: async (
    days: number = 90,
    ownerIds?: string[],
    source?: string,
    organizationId?: string,
  ): Promise<LeadFunnelReport> => {
    const params: Record<string, string | number | string[]> = { days };
    if (ownerIds && ownerIds.length > 0) params.owner_ids = ownerIds;
    if (source) params.source = source;
    if (organizationId) params.organization_id = organizationId;
    const response = await axiosInstance.get(`${BASE_URL}/reports/lead-funnel`, { params });
    return response.data;
  },

  getForecast: async (pipelineId?: string, monthsAhead: number = 3, ownerIds?: string[]): Promise<ForecastReport> => {
    const params: Record<string, string | number> = { months_ahead: monthsAhead };
    if (pipelineId) params.pipeline_id = pipelineId;
    addOwnerIds(params, ownerIds);
    const response = await axiosInstance.get(`${BASE_URL}/reports/forecast`, { params });
    return response.data.data;
  },

  getRevenueTrends: async (pipelineId?: string, days: number = 365, ownerIds?: string[]): Promise<RevenueTrendsReport> => {
    const params: Record<string, string | number> = { days };
    if (pipelineId) params.pipeline_id = pipelineId;
    addOwnerIds(params, ownerIds);
    const response = await axiosInstance.get(`${BASE_URL}/reports/revenue-trends`, { params });
    return response.data.data;
  },

  getLeaderboard: async (pipelineId?: string, days: number = 90, ownerIds?: string[]): Promise<LeaderboardEntry[]> => {
    const params: Record<string, string | number> = { days };
    if (pipelineId) params.pipeline_id = pipelineId;
    addOwnerIds(params, ownerIds);
    const response = await axiosInstance.get(`${BASE_URL}/reports/leaderboard`, { params });
    return response.data.data;
  },

  getTeamPerformance: async (pipelineId?: string, days: number = 90): Promise<TeamPerformanceMember[]> => {
    const params: Record<string, string | number> = { days };
    if (pipelineId) params.pipeline_id = pipelineId;
    const response = await axiosInstance.get(`${BASE_URL}/reports/team-performance`, { params });
    return response.data.data;
  },
};

// Sales Tenant Settings type
export interface SalesTenantSettings {
  id: string | null;
  tenant_id: string;
  custom_contact_roles: string[];
  settings: Record<string, unknown>;
  updated_by: string | null;
  prospecting_daily_send_limit?: number;
  prospecting_mailbox_daily_send_limit?: number;
  prospecting_send_window_start?: string;
  prospecting_send_window_end?: string;
  prospecting_skip_weekends?: boolean;
  /** Lead-funnel automation. Absent means ON — the backend's own default. */
  auto_advance_lead_status?: boolean;
}

// Settings API
export const salesSettingsApi = {
  getTenantSettings: async (): Promise<SalesTenantSettings> => {
    const response = await axiosInstance.get(`${BASE_URL}/settings/tenant`);
    return response.data;
  },

  updateTenantSettings: async (data: Partial<SalesTenantSettings>): Promise<SalesTenantSettings> => {
    const response = await axiosInstance.put(`${BASE_URL}/settings/tenant`, data);
    return response.data;
  },

  addCustomRole: async (role: string): Promise<string[]> => {
    const response = await axiosInstance.post(`${BASE_URL}/settings/contact-roles`, { role });
    return response.data.data;
  },

  deleteCustomRole: async (role: string): Promise<string[]> => {
    const response = await axiosInstance.delete(`${BASE_URL}/settings/contact-roles/${encodeURIComponent(role)}`);
    return response.data.data;
  },
};
